import { useContext } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { LocalizationContext } from 'localization';
import {
  CREATE_SERVICE,
  DELETE_SERVICE,
  GET_SERVICES,
  UPDATE_SERVICE,
} from 'schemas';
import notification from 'services/notificationService';
import { TService } from 'types';

import {
  IReturnValue,
  TCreateService,
  TDeleteService,
  TUpdateService,
} from './interfaces';

export const useServicesRequests = (): IReturnValue => {
  const { t } = useContext(LocalizationContext);
  const { data, loading } = useQuery<{
    services: Array<TService>;
  }>(GET_SERVICES, {
    fetchPolicy: 'no-cache',
  });

  const [createServiceRequest] = useMutation<void>(CREATE_SERVICE, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GET_SERVICES],
    onCompleted: () => {
      notification.info({
        t,
        title: t('service.created'),
        type: 'success',
        dismiss: 5,
      });
    },
  });
  const createService: TCreateService = ({
    title,
    cost,
    use_global_discounts,
    ean,
    NDS,
  }) =>
    createServiceRequest({
      variables: {
        input: {
          title,
          cost,
          use_global_discounts,
          ean,
          wholesale_cost: 0,
          ...(NDS ? { vat: NDS } : {}),
        },
      },
    });

  const [updateServiceRequest] = useMutation(UPDATE_SERVICE, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GET_SERVICES],
    onCompleted: () =>
      notification.info({
        t,
        title: t('service.updated'),
        type: 'success',
        dismiss: 5,
      }),
  });
  const updateService: TUpdateService = ({
    id,
    title,
    cost,
    use_global_discounts,
    ean,
    NDS,
  }) =>
    updateServiceRequest({
      variables: {
        id,
        input: {
          title,
          cost,
          use_global_discounts,
          ean,
          wholesale_cost: 0,
          ...(NDS ? { vat: NDS } : {}),
        },
      },
    });

  const [deleteServiceRequest] = useMutation(DELETE_SERVICE, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GET_SERVICES],
    onCompleted: () =>
      notification.info({
        t,
        title: t('service.deleted'),
        type: 'success',
        dismiss: 5,
      }),
  });
  const deleteService: TDeleteService = (id) =>
    deleteServiceRequest({ variables: { id } });

  return {
    loadedServices: data?.services ?? [],
    isServicesLoading: loading,
    createService,
    updateService,
    deleteService,
  };
};
